import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { TeamMembersSection } from '../components/settings/TeamMembersSection';
import { DeleteAccountSection } from '../components/settings/DeleteAccountSection';
import { BrandingSection } from '../components/settings/BrandingSection';
import { useAppSession } from '../hooks/useAppSession';
import { useApiMutation } from '../hooks/useApiMutation';
import { useBillingInfo } from '../hooks/useBillingInfo';
import { usersApi } from '../lib/api';

export function SettingsPage() {
  const { data: session } = useAppSession();
  const canManageBranding = session?.team?.role === 'owner' || session?.team?.role === 'admin';
  const { data: billingInfo, isLoading: billingLoading, error: billingError } = useBillingInfo();

  const [name, setName] = useState('');
  const [hasChanges, setHasChanges] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Populate from session when it loads
  useEffect(() => {
    if (session?.user) {
      setName(session.user.name);
    }
  }, [session?.user]);

  // Detect changes against the session data
  useEffect(() => {
    if (!session?.user) return;
    setHasChanges(name !== session.user.name);
    setSaveSuccess(false);
  }, [name, session?.user]);

  const saveMutation = useApiMutation<unknown, { name: string }>({
    mutationFn: (token, data) =>
      usersApi.updateProfile(token, session!.user.id, data),
    invalidateKeys: [['app-session']],
    onSuccess: () => {
      setSaveSuccess(true);
      setHasChanges(false);
    },
  });

  const handleSave = () => {
    if (!name.trim()) return;
    saveMutation.mutate({ name: name.trim() });
  };

  return (
    <section id="settings" className="page-content">
      <div className="page-content__inner">

      <div className="space-y-6">
        {/* Profile */}
        <section id="profile" className="card-sm">
          <h2 className="section-label mb-3">Profile</h2>
          <div className="space-y-6 mt-4">
            <div>
              <label htmlFor="profile-name" className="field-label">Name</label>
              <input
                id="profile-name"
                type="text"
                className="input"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>
            <div>
              <label htmlFor="profile-email" className="field-label">Email</label>
              <input
                id="profile-email"
                type="email"
                className="input"
                value={session?.user?.email ?? ''}
                disabled
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                className="btn-primary"
                disabled={!hasChanges || saveMutation.isPending || !name.trim()}
                onClick={handleSave}
              >
                <span className="text-cap-trim">{saveMutation.isPending ? 'Saving...' : 'Save changes'}</span>
              </button>
              {saveSuccess && (
                <span className="text-sm text-green-600">Saved</span>
              )}
              {saveMutation.isError && (
                <span className="text-sm text-red-600">
                  {saveMutation.error?.message || 'Failed to save'}
                </span>
              )}
            </div>
          </div>
        </section>

        {/* Team */}
        <section id="team" className="card-sm">
          <h2 className="section-label mb-3">Team</h2>
          <div className="mt-4">
            <TeamMembersSection />
          </div>
        </section>

        {/* Billing. Entry-point decision 2026-08-12: sidebar item + this card,
            visible to every member (BillingPage renders its own non-owner
            state), after zero persistent Billing affordances existed anywhere
            in the dashboard. The link must render regardless of billing-info
            load state — a failed summary must never remove the entry point. */}
        {canManageBranding && (
          <section id="branding" className="card-sm">
            <h2 className="section-label mb-3">Branding</h2>
            <BrandingSection />
          </section>
        )}

        <section id="billing" className="card-sm">
          <h2 className="section-label mb-3">Billing</h2>
          <div className="mt-4 flex items-center justify-between gap-4">
            <div>
              <p className="text-sm text-gray-500">Current plan</p>
              <p className="text-sm font-medium text-gray-900">
                {billingError
                  ? 'Plan details unavailable'
                  : billingLoading
                    ? 'Loading...'
                    : billingInfo?.planName ?? 'Plan details unavailable'}
              </p>
            </div>
            <Link to="/billing" className="btn-secondary flex-shrink-0">
              <span className="text-cap-trim">Manage plan &amp; seats</span>
            </Link>
          </div>
        </section>

        {/* Danger zone */}
        <section id="danger" className="card-sm">
          <h2 className="section-label mb-3">Danger zone</h2>
          <div className="mt-4">
            <DeleteAccountSection />
          </div>
        </section>

      </div>
      </div>
    </section>
  );
}
